import { Fragment, type HTMLAttributes } from 'react';

import {
  type DialogCloseProps,
  type DialogDescriptionProps,
  type DialogOverlayProps,
  type DialogPortalProps,
  type DialogProps,
  Dialog as DialogRoot,
  Close as DialogRootClose,
  Content as DialogRootContent,
  Description as DialogRootDescription,
  Overlay as DialogRootOverlay,
  Portal as DialogRootPortal,
  Title as DialogRootTitle,
  Trigger as DialogRootTrigger,
  type DialogTitleProps,
  type DialogTriggerProps,
  type DialogContentProps as PrimitiveDialogContentProps,
} from '@radix-ui/react-dialog';

import Divider from '@cobliteam/ui/components/Divider';

import CloseIcon from '@mui/icons-material/Close';

import cn from '../../utils/cn';
import Button from '../Button';
import Typography from '../Typography';

const Dialog = (props: DialogProps) => <DialogRoot {...props} />;

const DialogClose = (props: DialogCloseProps) => {
  if (props.asChild) return <DialogRootClose {...props}>{props.children}</DialogRootClose>;

  return (
    <DialogRootClose {...props} asChild>
      <Button aria-label='Close' icon={<CloseIcon className='h-6 w-6' />} variant='link' color='secondary' size='auto' />
    </DialogRootClose>
  );
};

const DialogTrigger = (props: DialogTriggerProps) => <DialogRootTrigger {...props} />;

const DialogPortal = (props: DialogPortalProps) => <DialogRootPortal {...props} />;

const DialogOverlay = ({ className, ...props }: DialogOverlayProps) => (
  <DialogRootOverlay
    className={cn(
      'fixed inset-0 bottom-0 left-0 right-0 top-0 z-modal grid place-items-center overflow-y-auto bg-gray-black/60 p-6 duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
);

interface DialogContentProps extends PrimitiveDialogContentProps {
  portal?: boolean;
  modalSize?: 'sm' | 'md' | 'lg';
}

const DialogContent = ({ className, children, portal = true, modalSize = 'md', ...props }: DialogContentProps) => {
  const sizes = {
    sm: 'modal-sm',
    md: 'modal-md',
    lg: 'modal-lg',
  } as const;

  const size = sizes[modalSize];

  const Wrapper = portal ? DialogPortal : Fragment;

  return (
    <Wrapper>
      <DialogOverlay>
        <DialogRootContent className={cn(`z-modal bg-white ${size} gap-4 bg-white py-5 shadow-modal sm:rounded-lg`, className)} {...props}>
          {children}
        </DialogRootContent>
      </DialogOverlay>
    </Wrapper>
  );
};

const DialogHeader = ({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className='flex flex-col gap-2'>
    <div className={cn('flex items-center justify-between px-5', className)} {...props}>
      {children}
    </div>
    <Divider />
  </div>
);

const DialogBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col px-5 py-2.5', className)} {...props} />;

const DialogFooter = ({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className='flex flex-col'>
    <Divider />
    <div {...props} className={cn('flex px-5 pt-5', className)}>
      {children}
    </div>
  </div>
);

const DialogTitle = ({ className, children, ...props }: DialogTitleProps) => (
  <DialogRootTitle className={className} {...props}>
    <Typography variant='heading-2'> {children} </Typography>
  </DialogRootTitle>
);

const DialogDescription = ({ className, ...props }: DialogDescriptionProps) => <DialogRootDescription className={cn('text-muted-foreground text-sm', className)} {...props} />;

Dialog.Portal = DialogPortal;
Dialog.Overlay = DialogOverlay;
Dialog.Close = DialogClose;
Dialog.Trigger = DialogTrigger;
Dialog.Content = DialogContent;
Dialog.Header = DialogHeader;
Dialog.Footer = DialogFooter;
Dialog.Body = DialogBody;
Dialog.Title = DialogTitle;
Dialog.Description = DialogDescription;

export default Dialog;
